/**
 * Three-bar (hamburger) menu for phone — nav to all product pages.
 * Used on vertical table chrome and as overflow for mobile shell.
 */
import { useEffect } from "react";
import { useLocation } from "wouter";
import {
  Coins,
  Home,
  LayoutGrid,
  LogIn,
  Shield,
  Spade,
  User,
  Wallet,
  X,
  FileText,
  Scale,
  Sparkles,
} from "lucide-react";
import { useWalletState } from "@/contexts/wallet-context";
import { loginWithGrudgeId } from "@/lib/grudge-auth";

const LINKS: {
  path: string;
  label: string;
  icon: typeof Home;
  action?: "wallet" | "grudge_id";
}[] = [
  { path: "/lobby", label: "Tables · lobby", icon: LayoutGrid },
  { path: "/gbuxgames", label: "GBUX Games", icon: Coins },
  { path: "/blackjack", label: "Blackjack", icon: Spade },
  { path: "/slots", label: "Slots", icon: Coins },
  { path: "/account", label: "Account", icon: User },
  {
    path: "https://id.grudge-studio.com/login",
    label: "Grudge ID login",
    icon: LogIn,
    action: "grudge_id",
  },
  { path: "/connect", label: "Wallet · Phantom", icon: Wallet, action: "wallet" },
  { path: "/callingpros", label: "Calling Pros", icon: Sparkles },
  { path: "/", label: "Home", icon: Home },
  { path: "/privacy", label: "Privacy", icon: FileText },
  { path: "/terms", label: "Terms", icon: Scale },
];

export function PhoneHamburgerMenu({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [loc, setLocation] = useLocation();
  const { wallet, openWalletPanel } = useWalletState();
  const isAdmin =
    wallet.publicKey === "98jzgFFkPhrw9sfr5YyttTpCBiJyid6tzxxJjXrj7xXK";

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        display: "flex",
      }}
    >
      <button
        type="button"
        aria-label="Close menu backdrop"
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          border: "none",
          background: "rgba(0,0,0,0.62)",
          backdropFilter: "blur(4px)",
        }}
      />
      <aside
        style={{
          position: "relative",
          width: "min(86vw, 320px)",
          height: "100%",
          background: "linear-gradient(180deg, #0c1224 0%, #060910 100%)",
          borderRight: "1px solid rgba(212,175,55,0.28)",
          padding:
            "max(16px, env(safe-area-inset-top)) 14px max(16px, env(safe-area-inset-bottom))",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          boxShadow: "8px 0 40px rgba(0,0,0,0.5)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <img
            src="/media/budb-pkr-logo-mark.jpg"
            data-brand="logo-mark"
            alt="BUDB PKR"
            style={{ height: 36, objectFit: "contain", borderRadius: 6 }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.06)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
            }}
          >
            <X size={20} />
          </button>
        </div>

        <p
          style={{
            color: "rgba(255,255,255,0.45)",
            fontSize: 11,
            margin: "0 0 4px",
          }}
        >
          Navigate · same live tables on phone &amp; desktop
        </p>

        {LINKS.map((item) => {
          const Icon = item.icon;
          const active =
            item.path === "/"
              ? loc === "/"
              : loc === item.path || loc.startsWith(item.path + "/");
          return (
            <button
              key={item.path}
              type="button"
              onClick={() => {
                if (item.action === "wallet") {
                  window.dispatchEvent(new Event("budb-open-wallet-sheet"));
                  openWalletPanel(wallet.connected ? "overview" : "connect");
                  onClose();
                  return;
                }
                if (item.action === "grudge_id") {
                  loginWithGrudgeId("/lobby");
                  onClose();
                  return;
                }
                setLocation(item.path);
                onClose();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                minHeight: 48,
                padding: "10px 12px",
                borderRadius: 12,
                border: active
                  ? "1px solid rgba(232,199,106,0.45)"
                  : "1px solid transparent",
                background: active
                  ? "rgba(232,199,106,0.12)"
                  : "rgba(255,255,255,0.04)",
                color: active ? "#e8c76a" : "rgba(255,255,255,0.88)",
                fontWeight: 600,
                fontSize: 15,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <Icon size={20} />
              {item.label}
            </button>
          );
        })}

        {isAdmin && (
          <button
            type="button"
            onClick={() => {
              setLocation("/admin");
              onClose();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              minHeight: 48,
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(168,85,247,0.35)",
              background: "rgba(168,85,247,0.1)",
              color: "#c4b5fd",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            <Shield size={20} />
            Admin
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            openWalletPanel("deposit");
            onClose();
          }}
          style={{
            marginTop: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            minHeight: 48,
            borderRadius: 12,
            border: "none",
            background: "linear-gradient(135deg, #b8860b, #e8c76a)",
            color: "#1a1200",
            fontWeight: 800,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          <Wallet size={18} />
          Play GBUX / deposit
        </button>
      </aside>
    </div>
  );
}

/** 44×44 three-bar trigger */
export function HamburgerButton({
  onClick,
  light,
}: {
  onClick: () => void;
  light?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open menu"
      data-testid="button-hamburger"
      style={{
        width: 44,
        height: 44,
        borderRadius: 10,
        border: light
          ? "1px solid rgba(255,255,255,0.14)"
          : "1px solid rgba(212,175,55,0.25)",
        background: "rgba(0,0,0,0.45)",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 5,
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 18,
          height: 2,
          background: "#e8c76a",
          borderRadius: 1,
        }}
      />
      <span
        style={{
          width: 18,
          height: 2,
          background: "#e8c76a",
          borderRadius: 1,
        }}
      />
      <span
        style={{
          width: 18,
          height: 2,
          background: "#e8c76a",
          borderRadius: 1,
        }}
      />
    </button>
  );
}
